import type { DefaultTheme } from 'styled-components';

import { CampaignStage } from '@/services/ws-digital/src/main/resources/rest-api';

import type { DatasetEntry, Option } from './types';

type DatasetSourceBase<TParam extends string> = {
  [key in TParam]: number | null;
};

export type DatasetSource<TParam extends string> = DatasetSourceBase<TParam> & {
  date: string;
};

export const splitText = (text?: string): string[] => {
  if (!text) {
    return [];
  }

  return text
    .split(/\r?\n|;|,/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
};

const compose = <T>(fn1: (a: T) => T, ...fns: Array<(a: T) => T>) => fns.reduce((prevFn, nextFn) => (value) => prevFn(nextFn(value)), fn1);

const trimArray =
  <TItem>(test: (item: TItem) => boolean, reduce = Array.prototype.reduce) =>
  (items: TItem[]): TItem[] => {
    let isStarted = false;

    return reduce.apply<
      TItem[],
      [callbackfn: (previousValue: TItem[], currentValue: TItem, currentIndex: number) => TItem[], initialValue: TItem[]],
      TItem[]
    >(items, [
      (arr: TItem[], item: TItem): TItem[] => {
        const result = test(item);
        if (result) {
          isStarted = true;
        }
        if (isStarted) {
          arr.push(item);
        }
        return arr;
      },
      [] as TItem[],
    ]);
  };

export const toChartFormat =
  <TParam extends string, TItem extends DatasetSource<TParam>>(param: TParam) =>
  (items: TItem[]): DatasetEntry[] => {
    const test = (item: TItem): boolean => typeof item[param] !== 'undefined' && item[param] !== null;
    return compose(
      trimArray(test, Array.prototype.reduceRight),
      trimArray(test),
    )(items).map<DatasetEntry>((item) => ({
      x: item.date,
      y: item[param] ?? 0,
    }));
  };

export const getRandomString = () => Math.random().toString(36).substring(3);

export const scrollTo = (element: HTMLElement | null, offset = 150) => {
  if (element) {
    window.scrollTo(0, element.getBoundingClientRect().top + window.scrollY - offset);
  }
};

export const isNotValidTextField = (value: string | undefined): boolean => !value || value.trim() === '';

export const isIntegerString = (value: string): boolean => /^\d+$/.test(value);

export const removeHTMLTagsFromString = (string: string): string => string.replace(/(<([^>]+)>)/gi, '');

export const getDigitalCampaignStageColor = (stage: CampaignStage, theme: DefaultTheme): string => {
  switch (stage) {
    case CampaignStage.BOOKED:
      return theme.colors.positive100;
    case CampaignStage.PERFORMANCE:
      return theme.colors.positive100;
    case CampaignStage.OFFERED:
      return theme.colors.link100;
    case CampaignStage.DRAFT:
      return theme.colors.link100;
    case CampaignStage.PENDING_APPROVAL:
      return theme.colors.warning100;
    case CampaignStage.ALTERNATE_QUOTE:
      return theme.colors.warning100;
    case CampaignStage.ARCHIVED:
      return theme.colors.warning100;
    case CampaignStage.OFFER_DENIED:
      return theme.colors.critical100;
    case CampaignStage.PREPAYMENT:
      return theme.colors.positive100;
    default:
      return '';
  }
};

export const isNotNullOrUndefined = <T>(value: T | null | undefined): value is T => value !== null && value !== undefined;

export const openOpenLinkedinSearchPage = (searchString: string) => {
  window.open(`https://www.linkedin.com/search/results/all/?keywords=${searchString}`);
};

export const chunkArray = <T>(array: T[], size: number): T[][] =>
  array.reduce<T[][]>((acc, _, index) => {
    if (index % size === 0) acc.push(array.slice(index, index + size));
    return acc;
  }, []);

export const getOption = <T = string>(value?: T): Option<T> | null =>
  !value || (typeof value === 'string' && value.length === 0) ? null : { label: String(value), value };

/**
 * Counts the number of decimal places a number has.
 * @param value - the decimal value to count
 * @returns {number}
 */
export const countDecimalPlaces = (value: number): number => {
  if (!Number.isFinite(value)) return 0;

  let e = 1;
  let p = 0;
  while (Math.round(value * e) / e !== value) {
    e *= 10;
    p += 1;
  }
  return p;
};

export const getValueFromEnum = <T extends string>(enumObject: Record<string, T>, value?: string): T | undefined => {
  if (!value) {
    return undefined;
  }
  if (Object.values(enumObject).includes(value as T)) {
    return value as T;
  }
  return undefined;
};

export const isLoosePostalCode = (value: number | string | null | undefined): boolean => {
  if (value == null) return false;
  const v = String(value).trim().toUpperCase().replace(/\s+/g, ' ');
  return /^(?=.{3,12}$)[A-Z0-9]+(?:[ -]?[A-Z0-9]+)*$/.test(v);
};

export const parsePostalCodesArray = (value: unknown): string[] => {
  if (!value) return [];
  if (typeof value === 'string') {
    return splitText(value).filter((v) => isLoosePostalCode(v));
  }
  if (Array.isArray(value)) {
    return value.filter((v): v is string => typeof v === 'string' && isLoosePostalCode(v));
  }
  return [];
};
